import {appContext} from "../../badge2.context.js";
import {I18nService} from "../../domain/i18n.service.js";
import {AbContainer} from "../container/container.component.js";
import {AbSeal} from "../seal/seal.component.js";
import {AbBadgeMenu} from "../badge-menu/badge-menu.component.js";
import {AbHeaderBadgeTexts} from "../badge-texts/badge-texts.component.js";
import {AbBadgeCarousel} from "../badge-carousel/badge-carousel.component.js";
import {AbBadgeAccordion} from "../badge-accordion/badge-accordion.component.js";
import {AbContainerItem} from "../container/container-item.component.js";
import {AbButton} from "../../widgets/button/button.widget.js";
import {AbVerifiedDateBanner} from "../verified-date-banner/verified-date-banner.component.js";
import {i18n} from "../../domain/i18n.js";
import {ResponsivenessService} from "../../domain/responsiveness.service.js";
import {BaseComponent} from "../../base.component.js";
import {AbTuvMark} from "../tuv-mark/tuv-mark.component.js";
import {AbBrandImage} from "../brand-image/brand-image.component.js";
import { browserFeatures, goToLink } from '../../utils/browser-utils.js';
import {doNothing} from "../../utils/functional-utils.js";
import {BadgeService} from "../../domain/badge.service.js";
import { FeatureService } from '../../domain/feature.service.js';
import { BadgeCompositionService } from '../../domain/badge-composition.service.js';
import { standardBadgeBuilder } from '../../composition/builder/standard-badge-builder.js';
import { GlobalService } from '../../domain/global.service.js';

/**
 * @typedef {object} _AbComponent_Context
 * @property {string} language
 * @property {_I18nService_fn_getText} txt
 * @property {_I18nService_fn_getImage} img
 * @property {_I18nService_fn_getImage} imgGlobal
 * @property {function(date: Date): string} formatDate
 * @property {number} zIndex the base value, to be used as starting point for components requiring a higher/lower value
 * @property {Element} rootElement the HTML element used as root (e.g. with shadow dom, this will the the shadow root, without it will be document.body itself)
 * @property {BadgeComposition} composition
 */

/**
 * @typedef {RenderableProps} _AbBadge_Props
 * @property {Badge} badge the owner badge entity
 * @property {Element} parentElement the element in which the badge renders - must be provided in advance since at render time it's too late
 */

/**
 * @typedef {object} _AbBadge_State
 * @property {boolean} isMaximized
 * @property {boolean} shake
 * @property {boolean} slideUp
 */

// used to manage listeners on the badge element
const $touchStart = Symbol('$touchStart');
const $touchEnd = Symbol('$touchEnd');

/**
 * Root component for displaying a badge, in a unified approach.
 * The goal for this is to be a starting point for describing all the rules involved in a single place for easier
 *   maintenance and change (than the previous approach).
 * These rules generally depend on the {@link _BadgeType} of the current badge and affect the way the badge and its
 *   inner components are displayed and what functionality is available.
 * This component maintains a high-level view and delegates as many details as possible to sub-components.
 */
export class AbBadge extends BaseComponent {

  /** @type {_AbBadge_Props} */
  props;

  /**
   * @private
   * @type {_AbComponent_Context}
   */
  _ctx = {};

  /** @type {AbBadgeMenu} */
  _badgeMenu;

  /** @type {BadgeService} */
  _badgeService;

  /** @type {GlobalService} */
  _globalService;

  /** @type {ResponsivenessService} */
  _responsivenessService;

  /**
   * @param {_AbBadge_Props} props
   */
  constructor(props) {
    super(props);

    this._responsivenessService = appContext.obtain(ResponsivenessService);
    this._featureService = appContext.obtain(FeatureService);
    this._globalService = appContext.obtain(GlobalService);
    this._badgeService = appContext.obtain(BadgeService);
    this._compositionService = appContext.obtain(BadgeCompositionService);
    this.state = {
      shake: false,
      slideUp: false,
      isMaximized: false,
    };
    this._minimizing = false;
  }

  getChildContext() {
    return this._ctx;
  }

  _refreshContext(props) {
    // we probably would like to use the new context API instead of this way, but that would require preact 10x
    const i18nResolver = appContext.obtain(I18nService).forLanguage(props.badge.type.language);
    this._ctx.language = props.badge.type.language;
    this._ctx.txt = i18nResolver.getText;
    this._ctx.img = i18nResolver.getImagePath;
    this._ctx.imgGlobal = i18nResolver.getGlobalImagePath;

    this._ctx.formatDate = i18nResolver.formatDate;
    this._ctx.zIndex = props.badge.type.options.zIndex;
    this._ctx.rootElement = this.props.parentElement;
  }

  maximize() {
    if (!this.state.isMaximized) {
      this.setState({ ...this.state, isMaximized: true });
    }
  }

  minimize() {
    if (!this._ctx.composition.parts.menu.minimizeAction || this._ctx.composition.parts.menu.minimizeAction === 'minimize') {
      if (this.state.isMaximized) {
        this._minimizing = true;
        this.setState({...this.state, isMaximized: false});
        if (this._badgeMenu) {
          this._badgeMenu.minimize();
        }
      }
    } else if (this._ctx.composition.parts.menu.minimizeAction === 'deactivate') {
      this.setState({ ...this.state, slideUp: true });
      setTimeout(() => {
        this.props.badge.deactivate('minimize');
      }, 250);
    }
  }

  toggleMaximized() {
    if (this.state.isMaximized) {
      this.minimize();
    } else {
      this.maximize();
    }
  }

  componentDidMount() {
    super.componentDidMount();
    this._applyShake();
  }

  // noinspection JSCheckFunctionSignatures
  componentDidUpdate(props) {
    if (this.props.badge.type.platform === 'mobile' && this.props.badge.type.variant === 'mid') {
      this._responsivenessService.setFadeOnScroll(this.base, 25, 30, 1.0, 0.0);
    } else {
      this._responsivenessService.unsetFadeOnScroll(this.base);
    }
    if (!this.base[$touchStart]) {
      this.base[$touchStart] = e => e.stopPropagation();
      this.base.addEventListener('touchstart', this.base[$touchStart], browserFeatures.supportsPassive ? {passive: true} : false);
    }
    if (!this.base[$touchEnd]) {
      this.base[$touchEnd] = e => e.stopPropagation();
      this.base.addEventListener('touchend', this.base[$touchEnd], browserFeatures.supportsPassive ? {passive: true} : false);
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    if (this.base[$touchStart]) {
      this.base.removeEventListener('touchstart', this.base[$touchStart]);
      delete this.base[$touchStart];
    }
    if (this.base[$touchEnd]) {
      this.base.removeEventListener('touchend', this.base[$touchEnd]);
      delete this.base[$touchEnd];
    }
    this._responsivenessService.unsetFadeOnScroll(this.base);
  }

  disableOnProd = (evt) => {
    if (process.env.NODE_ENV === 'production'){
      evt.preventDefault();
    }
  };

  // apply shake animation once at the beginning, then no longer should apply
  _applyShake() {
    this._responsivenessService.onPageLoaded(() => {
      this.setState({ ...this.state, shake: true });
      this._responsivenessService.onAnimationFinished(this, () => this.setState({ ...this.state, shake: false }));
    }, 500);
  }

  // noinspection JSCheckFunctionSignatures
  /**
   * At render time, decide - based on all the business rules and given current badge - what to show, where and how.
   * @param {_AbBadge_Props} props
   * @param {_AbBadge_State} state
   * @returns {ComponentChild}
   */
  render(props, state) {
    this._refreshContext(props);

    /** @type {BadgeComposition} */
    const composition = this._compositionService.newComposition(props.badge, this, standardBadgeBuilder);
    this._ctx.composition = composition;

    if (composition.aspect.type === 'expanded' && !composition.constraints.alwaysMaximized) {
      this._responsivenessService.onClickOutside(this, () => this.minimize());
      this._responsivenessService.onScrollAway(() => this.minimize());
    }

    // used to avoid transition animation when size changes due to minimizing
    if (this._minimizing) {
      setTimeout(() => {
        this._minimizing = false;
        this.base.classList.remove('ab-minimizing');
      }, 0);
    }

    let badgeInfo = {
      type: composition.code
    }
    if (composition.input.product) {
      if (composition.input.product?.id) {
        badgeInfo.product = composition.input.product.id;
      }
      if (composition.input.product?.brand?.name) {
        badgeInfo.brand = composition.input.product?.brand?.name
      }
      if (composition.input.product?.brandSponsored) {
        badgeInfo.bs = true;
      }
      if (composition.input.product?.officialBrandShop) {
        badgeInfo.d2c = true;
      }
    }

    const mainBadge = <div
      className={[
          `ab-badge`,
          `ab-platform-${composition.badgeType.platform}`,
          `ab-plan-${composition.badgeType.plan !== "premium" ? composition.badgeType.plan : (composition.input.product ? "premium" : "start")}`,
          `ab-variant-${composition.badgeType.variant}`,
          `${composition.input.company.tuvApprovalDate ? 'ab-with-tuv' : ''}`,
          `${this._minimizing ? 'ab-minimizing' : ''}`,
          `${composition.constraints.canMaximize ? 'ab-can-maximize' : ''}`,
          `${composition.aspect.type === 'expanded' ? 'ab-is-maximized' : ''}`,
          `${(composition.aspect.fixedType ? `ab-badge-fixed ab-badge-fixed-${composition.aspect.fixedType}` : '')}`,
          `${state.shake ? 'ab-animate' : ''}`,
          `${state.slideUp ? 'ab-slide-up' : ''}`,
          `${composition.aspect.hasSpaceAbove ? 'ab-has-space-above' : ''}`,
          `${Object.entries(composition.flags).filter(_ => _[1]).map(([flag, flagValue]) => `ab-flag-${flag}` + (flagValue === true ? '' : `-${flagValue}`)).join(' ')}`,
          `${composition.aspect.flavor ? `ab-flavor_${composition.aspect.flavor}` : ''}`,
      ].filter(_ => !!_).join(' ')}
      data-badge-info={JSON.stringify(badgeInfo)}
      style={composition.aspect.style}
      onContextMenu={this.disableOnProd}
      onDragStart={this.disableOnProd}
    >
      {composition.container && composition.container.show &&
      <AbContainer type={composition.container.type} clickable={composition.constraints.canMaximize && composition.aspect.type === 'collapsed'} onClick={() => this.maximize()}>
        <AbContainerItem place="header-start">
          <AbSeal offset={composition.parts.aubySeal.offsetType} offsetBottom={composition.parts.aubySeal.offsetBottom} onClick={composition.parts.aubySeal.clickable ? () => goToLink(composition.input.certificateUrl) : doNothing} />
        </AbContainerItem>
        <AbContainerItem place="header-main">
          <div>
            {composition.parts.menu.show &&
            <AbBadgeMenu
              placement={composition.parts.menu.placement}
              onRef={badgeMenu => this._badgeMenu = badgeMenu}
              customButtons={composition.parts.menu.includeMinimize && <AbButton className="ab-minimize-button" type="icon" icon="close-icon.svg" onClick={() => this.minimize()} stopPropagation={true}/>}
              popupOffsetRight={composition.parts.menu.extraOffsetRight + (composition.parts.menu.includeMinimize ? 24 : 0)}
            />
            }
          </div>
          {composition.parts.maximizeArrow.show && <AbButton className="ab-maximize-arrow" type="icon" icon="arrow.svg" iconFlip={true} />}
          {composition.parts.headerText.show && <AbHeaderBadgeTexts company={composition.input.company} brands={composition.input.brands} center={composition.parts.headerText.center} textType={composition.parts.headerText.type} textTypeMinimalOption={composition.parts.headerText.minimalOption} certificateUrl={composition.input.certificateUrl} t0={composition.badgeType.t0} />}
          {composition.parts.headerImage.show && <div className="ab-product-brand-image"><AbBrandImage brand={composition.input.product.brand} /></div>}
          {(composition.parts.carousel.show && composition.parts.carousel.location === 'header') && <AbBadgeCarousel brands={composition.input.brands} size={composition.parts.carousel.size} />}
        </AbContainerItem>
        <AbContainerItem place="header-end">
          {composition.parts.tuvSeal.show && <AbTuvMark type="seal" />}
        </AbContainerItem>
        {composition.parts.contents.show &&
        <AbContainerItem place="contents">
          <AbVerifiedDateBanner size={composition.parts.dateBanner.size} company={composition.input.company} t0={composition.badgeType.t0} />
          {(composition.parts.carousel.show && composition.parts.carousel.location === 'contents') && <AbBadgeCarousel brands={composition.input.brands} size={composition.parts.carousel.size} showBottomText={composition.parts.carousel.includeBottomText} t0={composition.badgeType.t0} />}
          {composition.parts.accordion.show && <AbBadgeAccordion texts={composition.parts.contents.texts} company={composition.input.company} plan={composition.badgeType.plan} variant={composition.badgeType.variant} product={composition.input.product} size={composition.parts.accordion.size} useOverlay={composition.parts.accordion.useOverlay} showTuv={composition.parts.tuvSeal.showInAccordion || composition.parts.tuvSeal.show} />}
        </AbContainerItem>
        }
        {composition.parts.contents.show &&
        <AbContainerItem place="footer" zIndex={composition.parts.button.zIndex}>
          <AbButton className="ab-certificate" type="button" size={composition.parts.button.size} caption={composition.badgeType.t0 ? "" : i18n.footer.showCertificate} href={composition.input.certificateUrl} />
        </AbContainerItem>
        }
      </AbContainer>
      }

      {
        composition.parts.sealAsButton.show &&
        <div className="ab-seal-button" style={composition.parts.sealAsButton.style}>
          <AbSeal display={composition.parts.sealAsButton.display} onClick={() => composition.constraints.canMaximize ? this.toggleMaximized() : doNothing()} />
        </div>
      }
    </div>;

    if (this._globalService.verbose) {
      this._badgeService._logger.debug('debug/badge-rendered', `Rendered ${props.badge.toString()}`, () => ({ badge: props.badge.toInfo(), composition: composition.getRaw() }));
    }

    return (
      <div className="ab-badge-root">
        { mainBadge }
        {
          composition.parts.shadowZone.show &&
          <div className="ab-empty-zone" style={{width: composition.parts.shadowZone.width, height: composition.parts.shadowZone.height}} />
        }
      </div>
    );
  }

}
